<template>
  <table>
    <thead>
      <tr>
        <th @click="patchSorting({ sortBy: 'symbol' })">
          <div class="header-column">
            <span>Symbol</span>
            <i
              class="fas"
              :class="{
                'fa-sort': (sortBy !== 'symbol'),
                'fa-sort-amount-up': (sortBy === 'symbol' && sortOrder === 'ascending'),
                'fa-sort-amount-down': (sortBy === 'symbol' && sortOrder === 'descending')
              }"
            ></i>
          </div>
        </th>
        <th @click="patchSorting({ sortBy: 'value' })">
          <div class="header-column">
            <span>Value</span>
            <i
              class="fas"
              :class="{
                'fa-sort': (sortBy !== 'value'),
                'fa-sort-amount-up': (sortBy === 'value' && sortOrder === 'ascending'),
                'fa-sort-amount-down': (sortBy === 'value' && sortOrder === 'descending')
              }"
            ></i>
          </div>
        </th>
      </tr>
    </thead>
    <tbody>
      <tr v-for="row in sortedLeafNodes">
        <td>
          <div class="symbol-name-container">
            <div class="symbol">{{ row.symbol }}</div>
            <div class="name">{{ row.name }}</div>
          </div>

        </td>
        <td>
          <div
          class="value"
          :class="{
            'up': row.calculatedValues.value > 0,
            'down': row.calculatedValues.value < 0,
            'neutral': row.calculatedValues.value === 0
          }">{{ row.calculatedValues.display }}</div>
        </td>
      </tr>
    </tbody>
  </table>
</template>

<script lang="ts" setup>
  import { computed, ref } from 'vue';
  import { useMarketCarpetContext } from '@/ts/marketCarpetContext';

  const { currentNodeLeafNodes } = useMarketCarpetContext();

  const sortOrder = ref<'descending' | 'ascending'>('ascending');
  const sortBy = ref<'value' | 'symbol'>('value');

  const sortedLeafNodes = computed(() => {
    const directionMultiplier = sortOrder.value === 'ascending' ? 1 : -1;
    return currentNodeLeafNodes.value.toSorted((a, b) => {
      if (sortBy.value === 'value')
        return (b.calculatedValues.value - a.calculatedValues.value) * directionMultiplier;
      return (a.symbol < b.symbol ? -1 : 1) * directionMultiplier;
    });
  });

  function patchSorting({ sortOrder: sortOrderKey, sortBy: sortByKey }: { sortOrder?: 'ascending' | 'descending', sortBy?: 'value' | 'symbol' }) {
    if (sortByKey === sortBy.value && !sortOrderKey) {
      sortOrder.value = sortOrder.value === 'ascending' ? 'descending' : 'ascending';
      return;
    }

    if (sortOrderKey) {
      sortOrder.value = sortOrderKey;
    }

    if (sortByKey) {
      sortBy.value = sortByKey;
      sortOrder.value = 'ascending';
    }
  }
</script>

<style scoped>
  table {
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
  }

  thead {
    display: block;
    flex-shrink: 0;
    height: 26px;
    width: 100%;
    border: none;
    white-space: nowrap;
    text-transform: uppercase;
    font-family: 'Roboto Condensed', 'Lato', 'Helvetica Neue', Helvetica, Arial, sans-serif;
    font-size: 10px;
    background-color: var(--background-color);
    color: rgba(255, 255, 255, 0.85);
  }

  tr {
    display: flex;
  }

  tr td:first-child, tr th:first-child {
    flex: 2;
  }

  tr td:not(:first-child), tr th:not(:first-child) {
    font-weight: 800;
    flex: 1;
  }

  tbody {
    flex: 1;
    min-height: 0px;
    overflow-y: auto;
    overflow-x: hidden;
    display: block;
  }

  tbody::-webkit-scrollbar {
    display: none;
  }

  td:not(:first-child) {
    font-size: 12px;
  }

  td:not(:first-child) {
    display: flex;
    align-items: center;
    justify-content: right;
  }

  .header-column {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  thead tr th:first-child {
    width: 66%;
  }

  thead tr th {
    cursor: pointer;
    padding: 6px 6px 4px 6px;
    text-align: left;
    font-size: 10px;
    font-weight: bold;
  }

  .symbol-name-container {
    padding: 5px;
  }

  .symbol-name-container .symbol {
    font-weight: 900;
    font-size: 12px;
    color: #2d3339;
  }

  .symbol-name-container .name {
    font-size: 9px;
    max-width: 100px;
    color: var(--background-color);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .value {
    font-size: 12px;
    padding: 5px;
    text-align: right;
    font-weight: 800;
  }

  .up {
    color: var(--up-color);
  }

  .down {
    color: var(--down-color);
  }

  tbody tr {
    border-bottom: 1px solid var(--border-color);
  }
</style>
